import { Badge, Box, Button, ButtonGroup, BadgeProps, Skeleton } from "@chakra-ui/react";
import { CollecticonTickSmall, CollecticonXmarkSmall } from "@devseed-ui/collecticons-chakra";

import { Loading, Overline, SceneThumbnail, ConfidenceTag } from ".";
import { Inference } from "../types/inferences";
import { formatDate, getLikelySpecies } from "../utils";
import { useModel } from "../context/AppConfig";

type DetectionInfoProps = {
  label: string;
  value: string | number;
  valueAsTag?: boolean | null;
}

function DetectionInfo({label, value, valueAsTag}: DetectionInfoProps) {
  return (
    <Box display="flex" as="dl" alignItems="center" gap="4">
      <Overline as="dt" fontWeight="500" opacity="0.675" flex="1">{label}</Overline>
      {valueAsTag ? <ConfidenceTag value={value} /> : <Box as="dd">{value}</Box>}
    </Box>
  );
}

type ValidatedBadgeProps = {
  isValid: boolean | null;
} & BadgeProps
export function ValidatedBadge({ isValid, ...props }: ValidatedBadgeProps) {
  return (
    <Badge
      zIndex="1000"
      variant="subtle"
      colorScheme={isValid ? "green" : "red"}
      size="sm"
      {...props}
    >
      {isValid ? "Valid" : "Invalid"}
    </Badge>
  );
}

type Props = {
  inference: Inference;
  validate?: (validationId: number, isValid: boolean) => void;
  isValidating?: boolean;
  includeThumbnail?: boolean;
  includeDate?: boolean;
  brightness?: number;
  contrast?: number;
}

function DetectionCard({ inference, validate, isValidating, includeThumbnail, includeDate, brightness, contrast}: Props) {
  const { id, is_valid, scene, model_id } = inference.properties;
  const model = useModel(model_id);
  const { species, confidence } = getLikelySpecies(inference);

  const isValidated = is_valid !== undefined && is_valid !== null;

  return (
    <Box fontSize="sm">
      {includeThumbnail && (
        <>
          {isValidated &&
          <ValidatedBadge
            isValid={is_valid}
            position="absolute"
            right="2"
            top="2"
          />}
          <SceneThumbnail width={250} height={120} bbox={inference.bbox} sceneId={scene.s3_prefix} brightness={brightness} contrast={contrast} />
        </>
      )}
      <Box px="3" my="3" display="flex" flexDir="column" gap="1">
        {includeDate && <DetectionInfo label="Date" value={formatDate(new Date(Date.parse(scene.date))).split(",")[0]} />}
        {model && <DetectionInfo label="Model" value={model?.name || ""} />}
        <DetectionInfo label="Species" value={species} />
        <DetectionInfo label="Confidence" value={confidence} valueAsTag />
      </Box>
      {isValidating && !isValidated && (
        <Loading
          display="flex"
          gap="2"
          justifyContent="center"
          fontSize="sm"
          opacity="0.75"
        >
          Updating validation
        </Loading>
      )}
      {validate && !isValidating && !isValidated && (
        <ButtonGroup size="sm" px="3" pb="3" isAttached width="100%">
          <Button
            flex="1"
            aria-label="Reject detection"
            title="Reject detection"
            colorScheme="red"
            variant="ghost"
            onClick={() => validate(id, false)}
            isDisabled={isValidating}
          >
            <CollecticonXmarkSmall />
          </Button>
          <Button
            flex="1"
            aria-label="Confirm detection"
            title="Confirm detection"
            colorScheme="green"
            variant="solid"
            onClick={() => validate(id, true)}
            isDisabled={isValidating}
          >
            <CollecticonTickSmall />
          </Button>
        </ButtonGroup>
      )}
    </Box>
  );
}

export default DetectionCard;

export function LoadingDetectionCard({ noData }: { noData?: boolean }) {
  return (
    <Box fontSize="sm">
      <Skeleton height="120px" speed={noData ? 1000 : 0.8} />
      <Box px="3" my="3" display="flex" flexDir="column" gap="2">
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="16" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="24" speed={noData ? 1000 : 0.8} />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="10" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="8" speed={noData ? 1000 : 0.8} />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Skeleton height="3" width="12" speed={noData ? 1000 : 0.8} />
          <Skeleton height="3" width="16" speed={noData ? 1000 : 0.8} />
        </Box>
      </Box>
    </Box>
  );
}
